import React, { useEffect, useState } from 'react';
import { matchPath } from 'react-router';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import styled from 'styled-components';
import Paths from 'AppPaths';
import CreateEventContentDropDown from 'components/CreateEventContentDropDown';
import FSObjectList from 'components/event/FileSystem/FSObjectList';
import BreadcrumbTrail from 'components/event/FileSystem/BreadcrumbTrail';
import FileSystemSearchInput from 'components/event/FileSystem/FileSystemSearchInput';
import EventDashUploadStatusPane from './EventDashUploadStatusPane';
import { RoleHelper } from '@shoflo/polymer/helpers/RoleHelper/RoleHelper';
import ViewRelativePaths from 'constants/CuesheetViews';
import { BROADCAST_FORM } from 'constants/ModalTypes';
import { TypePermissionLookup } from 'constants/Permissions';
import { hasActivePermission } from 'lib/permissionHelper';

import {
  getFSObjectChildren,
  getFSObject,
  getFSObjectByPath,
  setFSObjectSorting,
  downloadFSDoc,
  moveFSObjects,
  moveFSObjectsToPath,
  multiSelectFSObject,
  searchFSObjects,
  clearFSObjectsSearchResults,
  uploadEventDocs,
  archiveFSObject
} from 'actions/Event';

import { copyInviteLink, duplicateBroadcast } from 'actions/Broadcast';

import { showModal } from 'actions';
import {
  isDirectory,
  isDocFSObject,
  getFSRefObjectId
} from 'components/event/FileSystem/fsObjectUtils';
import { isStudioFSObject } from '../components/event/FileSystem/fsObjectUtils';

const OptionsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 0 !important;

  @media only screen and (max-width: 556px) {
    position: sticky;
    transform: translateX(110px);
  }
`;

const EventDashFoldersPage = props => {
  const {
    showModal,
    folderId,
    selectedItemIds,
    getFSObject,
    getFSObjectByPath,
    getFSObjectChildren,
    moveFSObjects,
    moveFSObjectsToPath,
    multiSelectFSObject,
    searchFSObjects,
    uploadEventDocs,
    clearFSObjectsSearchResults,
    searchResults,
    downloadFSDoc,
    archiveFSObject,
    onSortChange,
    sorting,
    folder,
    items,
    history,
    teamId,
    eventId,
    user,
    isLoading,
    team,
    copyInviteLink,
    duplicateBroadcast,
    event
  } = props;

  const isEventAdmin = RoleHelper.hasEventAdminRole(user, teamId, eventId);

  /**
   * Get the children (files) of the current folder
   */
  useEffect(() => {
    getFSObjectChildren(teamId, eventId, folderId);
  }, [getFSObjectChildren, teamId, eventId, folderId]);

  /**
   * If there is a folderId passed, we gotta fetch the deets
   */
  useEffect(() => {
    if (folderId) getFSObject(teamId, eventId, folderId);
  }, [getFSObject, teamId, eventId, folderId]);

  const [searchTerm, setSearchTerm] = useState('');

  const userViewPreference = user.preferences && user.preferences.cuesheetView;
  const viewPath = userViewPreference
    ? ViewRelativePaths[userViewPreference]
    : ViewRelativePaths.Compact;

  const onItemClick = (item, e) => {
    handleRoutePermissionAccess(
      item,
      user,
      team,
      itemClickHandler.bind(
        null,
        history,
        downloadFSDoc,
        multiSelectFSObject,
        teamId,
        eventId,
        viewPath,
        item,
        e
      )
    );
  };

  const onBroadcastCreateClick = () => {
    handleCreatePermissionAccess(
      'broadcast',
      team,
      showModal.bind(null, BROADCAST_FORM, null, event)
    );
  };

  const onSelectItem = selectItem.bind(null, multiSelectFSObject);

  const onDocsUploaded = uploadEventDocs.bind(null, teamId, eventId);

  const eventDashOptionsNode = (
    <OptionsWrapper>
      <FileSystemSearchInput
        onChange={onSearchTermChange.bind(
          null,
          teamId,
          eventId,
          setSearchTerm,
          searchFSObjects,
          clearFSObjectsSearchResults
        )}
        onResultClick={onItemClick}
        onCancel={onCancelSearch.bind(
          null,
          setSearchTerm,
          clearFSObjectsSearchResults
        )}
        value={searchTerm}
        results={searchResults}
      />
      {isEventAdmin && (
        <CreateEventContentDropDown
          onBroadcastCreate={onBroadcastCreateClick}
          onDocsUploaded={onDocsUploaded}
          onFolderCreate={showModal.bind(null, 'CREATE_FS_FOLDER', {
            teamId,
            eventId
          })}
        />
      )}
    </OptionsWrapper>
  );

  let trail = null;

  if (folder) {
    trail = folder.name;
    const parent = folder.parentPath;
    if (parent) {
      trail = `${parent},${trail}`;
    }
  }

  const onItemDrop = moveFSObjects.bind(null, teamId, eventId);
  const onItemDroppedOnCrumb = moveFSObjectsToPath.bind(null, teamId, eventId);

  const onRootClick = redirectToEventDash.bind(null, history, teamId, eventId);

  return (
    <>
      <BreadcrumbTrail
        onRootDrop={onItemDrop}
        onCrumbDrop={onItemDroppedOnCrumb}
        onCrumbClick={getFSObjectByPath.bind(null, teamId, eventId)}
        onRootClick={onRootClick}
        trail={trail}
      />
      <FSObjectList
        {...{
          items,
          sorting,
          onSortChange,
          onItemClick,
          onItemDrop,
          onSelectItem,
          onArchiveClick: archiveFSObject,
          onEditBroadcastClick: showModal,
          onFilesDrop: onDocsUploaded,
          onUpdateFolderClick: showModal,
          onUpdateEventDocClick: showModal,
          selectedItemIds,
          eventDashOptionsNode,
          isEventAdmin,
          isLoading,
          onCopyInviteLink: copyInviteLink,
          onDuplicateBroadcast: duplicateBroadcast
        }}
      />
      <EventDashUploadStatusPane />
    </>
  );
};

export const handleRoutePermissionAccess = (
  item,
  user,
  team,
  handleItemClick
) => {
  // const { lastEventRole } = user;
  // const { availableTools } = lastEventRole;
  const { permissions } = team || {};

  if (isDirectory(item)) return handleItemClick();

  const { displayType } = item.reference;
  const permissionType = TypePermissionLookup[displayType];
  if (hasActivePermission(permissionType, permissions)) {
    return handleItemClick();
  }
};

export const handleCreatePermissionAccess = (type, team, handleItemCreate) => {
  const { permissions } = team;
  const permissionType = TypePermissionLookup[type];
  if (hasActivePermission(permissionType, permissions)) {
    handleItemCreate();
  }
};
/**
 * Handle click of an event file system object
 *
 * @param {*} history
 * @param {*} downloadFSDoc
 * @param {*} teamId
 * @param {*} eventId
 * @param {*} item
 */
export const itemClickHandler = (
  history,
  downloadFSDoc,
  multiSelectFSObject,
  teamId,
  eventId,
  viewPath,
  item,
  e
) => {
  e.stopPropagation();

  if (e.shiftKey && multiSelectFSObject) {
    return multiSelectFSObject(item._id);
  }

  if (isDirectory(item)) {
    window.scrollTo(0, 0);
    history.push(`/dash/team/${teamId}/event/${eventId}/folder/${item._id}`);
    return;
  }

  const refObjectId = getFSRefObjectId(item);

  if (isStudioFSObject(item)) {
    window.location.assign(
      `/studio-director/teams/${teamId}/events/${eventId}/broadcasts/${refObjectId}`
    );
    return;
  }

  if (isDocFSObject(item)) {
    downloadFSDoc(teamId, eventId, refObjectId);
    return;
  }
};

export const selectItem = (multiSelectFSObject, item, e) => {
  e.stopPropagation();

  const metaKeyPressed = e.nativeEvent.metaKey;
  const shiftKeyPressed = e.nativeEvent.shiftKey;
  const ctrlKeyPressed = e.nativeEvent.ctrlKey;

  // select multiple
  if (metaKeyPressed || shiftKeyPressed || ctrlKeyPressed) {
    return multiSelectFSObject(item._id);
  } else {
    // select single
    multiSelectFSObject(null); // clear all
    multiSelectFSObject(item._id);
  }
};

/**
 * Search cancel handler, exporting for ease of testing
 * @param {*} setSearchTerm
 * @param {*} clearFSObjectsSearchResults
 */
export const onCancelSearch = (setSearchTerm, clearFSObjectsSearchResults) => {
  setSearchTerm('');
  clearFSObjectsSearchResults();
};

/**
 * Dash redirect function, exported to test
 */
export const redirectToEventDash = (history, teamId, eventId) => {
  history.push(`/dash/team/${teamId}/event/${eventId}/dashboard`);
};

/**
 * Search term change handler...exporting for ease of testing
 */
export const onSearchTermChange = (
  teamId,
  eventId,
  setSearchTerm,
  searchFSObjects,
  clearFSObjectsSearchResults,
  e
) => {
  const value = e.target.value;
  setSearchTerm(value);
  if (value.length >= 3) {
    searchFSObjects(teamId, eventId, value);
  }
  if (value.length === 0) {
    clearFSObjectsSearchResults();
  }
};

const PropMapper = (
  {
    eventFSObjects,
    eventFSObjectsSorting,
    selectedEventFSFolder,
    multiSelectedFSObjects,
    eventFSObjectSearchResults,
    isFSObjectLoading,
    user,
    isTeamAdmin,
    team,
    event
  },
  { match }
) => {
  const eventMatch = matchPath(match.url, {
    path: Paths.EventDash
  });

  const teamIdEventId = {};

  if (eventMatch) {
    teamIdEventId.eventId = eventMatch.params.eventId;
    teamIdEventId.teamId = eventMatch.params.teamId;
  }

  const { folderId } = match.params;

  return {
    ...teamIdEventId,
    folderId,
    items: eventFSObjects,
    isLoading: isFSObjectLoading,
    sorting: eventFSObjectsSorting,
    folder: selectedEventFSFolder,
    selectedItemIds: multiSelectedFSObjects,
    searchResults: eventFSObjectSearchResults,
    user,
    isTeamAdmin,
    team,
    event
  };
};

export { PropMapper };

export default withRouter(
  connect(PropMapper, {
    showModal,
    getFSObject,
    getFSObjectByPath,
    getFSObjectChildren,
    onSortChange: setFSObjectSorting,
    downloadFSDoc,
    moveFSObjects,
    moveFSObjectsToPath,
    multiSelectFSObject,
    searchFSObjects,
    uploadEventDocs,
    clearFSObjectsSearchResults,
    archiveFSObject,
    copyInviteLink,
    duplicateBroadcast
  })(EventDashFoldersPage)
);
